import React, { useState } from 'react';
import { Phone, MessageSquare, Calendar, MapPin, Globe, Instagram, Facebook, CheckCircle2, AlertCircle, Building2, Euro, Check, Trophy } from 'lucide-react';
import { Lead } from '../../types/lead';
import { BookingModal } from '../booking/BookingModal';
import { useDataStore } from '../../context/DataStoreContext';
interface LeadContactBlockProps {
  lead: Lead;
}
export function LeadContactBlock({
  lead
}: LeadContactBlockProps) {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const {
    updateLead
  } = useDataStore();
  const fullAddress = [lead.DIRECCION.calle, lead.DIRECCION.numero, lead.DIRECCION.barrio, lead.DIRECCION.ciudad].filter(Boolean).join(', ');
  const initials = lead.DECISOR_NOMBRE ? lead.DECISOR_NOMBRE.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase() : '?';
  const hasPaidMicro = lead.STATE === 'Micro-acuerdo' || lead.STATE === 'LeadCualificado';
  const isQualified = lead.STATE === 'LeadCualificado';
  const toggleMicro = () => {
    if (isQualified) {
      // turning off micro while qualified -> drop to Nuevo (also drops qualification)
      updateLead(lead.id, {
        STATE: 'Nuevo'
      });
    } else if (hasPaidMicro) {
      updateLead(lead.id, {
        STATE: 'Nuevo'
      });
    } else {
      updateLead(lead.id, {
        STATE: 'Micro-acuerdo'
      });
    }
  };
  const toggleQualified = () => {
    if (isQualified) {
      // drop back to micro paid
      updateLead(lead.id, {
        STATE: 'Micro-acuerdo'
      });
    } else {
      updateLead(lead.id, {
        STATE: 'LeadCualificado'
      });
    }
  };
  const phoneCleaned = lead.TELEFONO_WHATSAPP.replace(/[^\d+]/g, '');
  const waHref = phoneCleaned ? `https://wa.me/${phoneCleaned.replace(/^\+/, '')}` : '#';
  const telHref = phoneCleaned ? `tel:${phoneCleaned}` : '#';
  return <div className="card overflow-hidden" data-id="element-268">
      {/* Header — business identity */}
      <div className="relative px-5 pt-6 pb-5 bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent border-b border-white/[0.04]" data-id="element-269">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-growthly-muted mb-4" data-id="element-270">
          <Building2 size={11} data-id="element-271" />
          Contacto Principal
        </div>

        <div className="flex items-start gap-3.5" data-id="element-272">
          <div className="relative flex-shrink-0" data-id="element-273">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white/20 to-white/[0.04] flex items-center justify-center text-white text-[15px] font-bold border border-white/10 shadow-lg shadow-black/30" data-id="element-274">
              {initials}
            </div>
            {lead.VERIFICADO && <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-growthly-green flex items-center justify-center border-2 border-growthly-surface" data-id="element-275">
                <Check size={11} className="text-white" strokeWidth={3} data-id="element-276" />
              </div>}
          </div>

          <div className="flex-1 min-w-0" data-id="element-277">
            <p className="text-[16px] font-semibold text-white leading-tight break-words" title={lead.DECISOR_NOMBRE} data-id="element-278">
              {lead.DECISOR_NOMBRE || <span className="text-growthly-muted italic font-normal" data-id="element-279">
                  Sin nombre
                </span>}
            </p>
            <p className="text-[12px] text-growthly-muted mt-1 break-words" title={lead.DECISOR_CARGO} data-id="element-280">
              {lead.DECISOR_CARGO || <span className="italic" data-id="element-281">Sin cargo</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Estado Comercial — two stacked toggles */}
      <div className="px-5 py-5 border-b border-white/[0.04] space-y-3" data-id="element-282">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-growthly-muted" data-id="element-283">
          Estado comercial
        </p>

        {/* Micro-acuerdo toggle */}
        <div className={`rounded-xl border transition-all ${hasPaidMicro ? 'bg-growthly-green/[0.07] border-growthly-green/25' : 'bg-white/[0.02] border-white/[0.06]'}`} data-id="element-284">
          <div className="flex items-center gap-3 p-3.5" data-id="element-285">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${hasPaidMicro ? 'bg-growthly-green/20 text-growthly-green' : 'bg-white/5 text-growthly-muted'}`} data-id="element-286">
              <Euro size={18} strokeWidth={2.2} data-id="element-287" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-288">
              <p className="text-[13px] font-semibold text-white leading-tight" data-id="element-289">
                Micro-acuerdo
              </p>
              <p className={`text-[11px] mt-0.5 leading-tight ${hasPaidMicro ? 'text-growthly-green' : 'text-growthly-muted'}`} data-id="element-290">
                {hasPaidMicro ? '✓ Pagó 50€' : 'Pendiente · 50€'}
              </p>
            </div>
            <button onClick={toggleMicro} role="switch" aria-checked={hasPaidMicro} aria-label="Marcar micro-acuerdo como pagado" className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${hasPaidMicro ? 'bg-growthly-green' : 'bg-white/15'}`} data-id="element-291">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md ${hasPaidMicro ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-292" />
            </button>
          </div>
        </div>

        {/* Lead Cualificado toggle */}
        <div className={`rounded-xl border transition-all ${isQualified ? 'bg-growthly-amber/[0.08] border-growthly-amber/30' : 'bg-white/[0.02] border-white/[0.06]'}`} data-id="element-293">
          <div className="flex items-center gap-3 p-3.5" data-id="element-294">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isQualified ? 'bg-growthly-amber/20 text-growthly-amber' : 'bg-white/5 text-growthly-muted'}`} data-id="element-295">
              <Trophy size={18} strokeWidth={2.2} data-id="element-296" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-297">
              <p className="text-[13px] font-semibold text-white leading-tight" data-id="element-298">
                Lead Cualificado
              </p>
              <p className={`text-[11px] mt-0.5 leading-tight ${isQualified ? 'text-growthly-amber' : 'text-growthly-muted'}`} data-id="element-299">
                {isQualified ? '✓ Pago completo del servicio' : 'Aún no pagó el servicio completo'}
              </p>
            </div>
            <button onClick={toggleQualified} role="switch" aria-checked={isQualified} aria-label="Marcar como Lead Cualificado" className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${isQualified ? 'bg-growthly-amber' : 'bg-white/15'}`} data-id="element-300">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md ${isQualified ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-301" />
            </button>
          </div>
        </div>
      </div>

      {/* Phone + verification */}
      <div className="px-5 py-5 border-b border-white/[0.04]" data-id="element-302">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-growthly-muted mb-3" data-id="element-303">
          Teléfono
        </p>

        <div className="flex items-center gap-2.5 mb-2.5 min-w-0" data-id="element-304">
          <Phone className="w-4 h-4 text-growthly-muted flex-shrink-0" data-id="element-305" />
          <span className="text-[14px] font-medium text-white truncate" title={lead.TELEFONO_WHATSAPP} data-id="element-306">
            {lead.TELEFONO_WHATSAPP || <span className="text-growthly-muted italic font-normal" data-id="element-307">
                Sin teléfono
              </span>}
          </span>
        </div>

        {lead.TELEFONO_WHATSAPP && <div className="mb-4" data-id="element-308">
            {lead.VERIFICADO ? <span className="inline-flex items-center text-[11px] font-medium text-growthly-green bg-growthly-green/10 px-2 py-1 rounded-md border border-growthly-green/20" data-id="element-309">
                <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-310" /> Verificado
              </span> : <span className="inline-flex items-center text-[11px] font-medium text-growthly-amber bg-growthly-amber/10 px-2 py-1 rounded-md border border-growthly-amber/20" data-id="element-311">
                <AlertCircle className="w-3 h-3 mr-1" data-id="element-312" /> No verificado
              </span>}
          </div>}

        <div className="grid grid-cols-3 gap-2" data-id="element-313">
          <a href={telHref} className="flex flex-col items-center justify-center py-3 px-2 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:bg-white/[0.08] hover:border-white/15 transition-all group" data-id="element-314">
            <Phone className="w-4 h-4 text-growthly-muted group-hover:text-white mb-1.5 transition-colors" data-id="element-315" />
            <span className="text-[11px] font-medium text-growthly-muted group-hover:text-white transition-colors" data-id="element-316">
              Llamar
            </span>
          </a>
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center py-3 px-2 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:bg-growthly-green/10 hover:border-growthly-green/30 transition-all group" data-id="element-317">
            <MessageSquare className="w-4 h-4 text-growthly-muted group-hover:text-growthly-green mb-1.5 transition-colors" data-id="element-318" />
            <span className="text-[11px] font-medium text-growthly-muted group-hover:text-growthly-green transition-colors" data-id="element-319">
              WhatsApp
            </span>
          </a>
          <button onClick={() => setIsBookingOpen(true)} className="flex flex-col items-center justify-center py-3 px-2 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:bg-white hover:border-white transition-all group" data-id="element-320">
            <Calendar className="w-4 h-4 text-growthly-muted group-hover:text-[#0B0B0B] mb-1.5 transition-colors" data-id="element-321" />
            <span className="text-[11px] font-medium text-growthly-muted group-hover:text-[#0B0B0B] transition-colors" data-id="element-322">
              Agendar
            </span>
          </button>
        </div>
      </div>

      {/* Address & links */}
      <div className="px-5 py-5 space-y-4" data-id="element-323">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-growthly-muted" data-id="element-324">
          Ubicación y enlaces
        </p>

        <div className="flex items-start gap-3" data-id="element-325">
          <div className="w-9 h-9 rounded-lg bg-white/[0.03] flex items-center justify-center flex-shrink-0 border border-white/[0.06]" data-id="element-326">
            <MapPin className="w-4 h-4 text-growthly-muted" data-id="element-327" />
          </div>
          <div className="flex-1 min-w-0 pt-2" data-id="element-328">
            <span className="text-[13px] text-growthly-text leading-relaxed break-words" data-id="element-329">
              {fullAddress || <span className="text-growthly-muted italic" data-id="element-330">
                  Sin dirección
                </span>}
            </span>
          </div>
        </div>

        {lead.ENLACE_GMB && <div className="flex items-start gap-3" data-id="element-331">
            <div className="w-9 h-9 rounded-lg bg-white/[0.03] flex items-center justify-center flex-shrink-0 border border-white/[0.06]" data-id="element-332">
              <Globe className="w-4 h-4 text-growthly-muted" data-id="element-333" />
            </div>
            <a href={lead.ENLACE_GMB} target="_blank" rel="noopener noreferrer" className="text-[13px] text-white hover:underline truncate pt-2 flex-1" data-id="element-334">
              Ficha Google Maps
            </a>
          </div>}

        {lead.RRSS_HANDLES.instagram && <div className="flex items-start gap-3" data-id="element-335">
            <div className="w-9 h-9 rounded-lg bg-white/[0.03] flex items-center justify-center flex-shrink-0 border border-white/[0.06]" data-id="element-336">
              <Instagram className="w-4 h-4 text-growthly-muted" data-id="element-337" />
            </div>
            <span className="text-[13px] text-growthly-text pt-2 truncate flex-1" data-id="element-338">
              {lead.RRSS_HANDLES.instagram}
            </span>
          </div>}

        {lead.RRSS_HANDLES.facebook && <div className="flex items-start gap-3" data-id="element-339">
            <div className="w-9 h-9 rounded-lg bg-white/[0.03] flex items-center justify-center flex-shrink-0 border border-white/[0.06]" data-id="element-340">
              <Facebook className="w-4 h-4 text-growthly-muted" data-id="element-341" />
            </div>
            <span className="text-[13px] text-growthly-text pt-2 truncate flex-1" data-id="element-342">
              {lead.RRSS_HANDLES.facebook}
            </span>
          </div>}
      </div>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} leadId={lead.id} leadName={lead.NOMBRE_NEGOCIO} data-id="element-343" />
    </div>;
}